/* eslint-disable @typescript-eslint/no-explicit-any */
import { AxiosResponse } from 'axios';
import { useCallback } from 'react';
import axios from './axios';
import { handleAxiosError, serverUrl } from './axios-service';
import { ContentCreateRequest, ContentResponse, ContentUpdateRequest } from '../types/content';

const baseUrl = `${serverUrl}/contents`;

const useContentApi = () => {
    // Create new content (Admin only)
    const createContent = useCallback(async (data: ContentCreateRequest): Promise<ContentResponse> => {
        try {
            const result: AxiosResponse<ContentResponse> = await axios.post(baseUrl, data);
            return result.data;
        } catch (error: any) {
            return handleAxiosError(error);
        }
    }, []);

    // Get content by ID (Public)
    const getContentById = useCallback(async (contentId: string): Promise<ContentResponse> => {
        try {
            const result: AxiosResponse<ContentResponse> = await axios.get(`${baseUrl}/${contentId}`);
            return result.data;
        } catch (error: any) {
            return handleAxiosError(error);
        }
    }, []);

    // Get content by key (Public)
    const getContentByKey = useCallback(async (key: string): Promise<ContentResponse> => {
        try {
            const result: AxiosResponse<ContentResponse> = await axios.get(`${baseUrl}/key/${key}`);
            return result.data;
        } catch (error: any) {
            return handleAxiosError(error);
        }
    }, []);

    // Get all content by type (Public)
    const getContentByType = useCallback(async (contentType: string): Promise<ContentResponse[]> => {
        try {
            const result: AxiosResponse<ContentResponse[]> = await axios.get(`${baseUrl}/type/${contentType}`);
            return result.data;
        } catch (error: any) {
            return handleAxiosError(error);
        }
    }, []);

    // Update content (Admin only)
    const updateContent = useCallback(async (contentId: string, data: ContentUpdateRequest): Promise<ContentResponse> => {
        try {
            const result: AxiosResponse<ContentResponse> = await axios.put(`${baseUrl}/${contentId}`, data);
            return result.data;
        } catch (error: any) {
            return handleAxiosError(error);
        }
    }, []);

    // Delete content (Admin only)
    const deleteContent = useCallback(async (contentId: string): Promise<void> => {
        try {
            await axios.delete(`${baseUrl}/${contentId}`);
        } catch (error: any) {
            return handleAxiosError(error);
        }
    }, []);

    // List all content with optional pagination and type filter (Public)
    const getContents = useCallback(async (skip: number = 0, limit: number = 1000, contentType?: string): Promise<ContentResponse[]> => {
        try {
            const params = { skip, limit, content_type: contentType };
            const result: AxiosResponse<ContentResponse[]> = await axios.get(baseUrl, { params });
            return result.data;
        } catch (error: any) {
            return handleAxiosError(error);
        }
    }, []);

    return {
        createContent,
        getContentById,
        getContentByKey,
        getContentByType,
        updateContent,
        deleteContent,
        getContents,
    };
};

export default useContentApi; 